// SPDX-License-Identifier: AGPL-3.0-or-later

package kairos

import kotlin.time.Clock
import kotlin.time.Instant
import kotlinx.serialization.Serializable

const val KAIROS_WORKSPACE_HEADER: String = "X-Kairos-Workspace"

@Serializable
data class KairosWorkspaceSnapshot(
    val workspace: Kairos.Workspace,
    val memberships: List<Kairos.WorkspaceMembership> = emptyList(),
    val sourceName: String,
    val users: List<Kairos.User>,
    val projects: List<Kairos.Project>,
    val tickets: List<Kairos.Ticket>,
    val ticketBlockers: List<Kairos.TicketBlocker> = emptyList(),
    val activities: List<Kairos.TicketActivity>,
    val objectives: List<Kairos.Objective> = emptyList(),
    val kpis: List<Kairos.Kpi> = emptyList(),
    val measurements: List<Kairos.KpiMeasurement> = emptyList(),
    val kpiEvaluations: List<KairosKpiEvaluation> = emptyList(),
    val timeboxes: List<Kairos.Timebox> = emptyList(),
    val routines: List<Kairos.Routine> = emptyList(),
    val events: List<Kairos.Event> = emptyList(),
)

@Serializable
data class KairosSessionInfo(
    val authenticated: Boolean,
    val user: Kairos.User? = null,
    val ownerUser: Kairos.User? = null,
    val currentWorkspace: Kairos.Workspace? = null,
    val availableWorkspaces: List<Kairos.Workspace> = emptyList(),
    val availableWorkspaceAccess: List<KairosWorkspaceAccess> = emptyList(),
    val currentWorkspaceRole: Kairos.WorkspaceMembershipRole? = null,
    val tokenPreview: String? = null,
)

@Serializable
data class KairosWorkspaceAccess(
    val workspace: Kairos.Workspace,
    val role: Kairos.WorkspaceMembershipRole,
)

@Serializable
data class KairosWorkspaceMember(
    val user: Kairos.User,
    val username: String,
    val role: Kairos.WorkspaceMembershipRole,
    val createdAt: Instant,
    val ownerUser: Kairos.User? = null,
    val ownerUsername: String? = null,
)

@Serializable
data class KairosAgentSubaccount(
    val user: Kairos.User,
    val username: String,
    val ownerUser: Kairos.User,
    val workspaceAccess: List<KairosAgentWorkspaceAccess> = emptyList(),
    val lastSeenAt: Instant? = null,
)

@Serializable
data class KairosAgentWorkspaceAccess(
    val workspace: Kairos.Workspace,
    val configuredRole: Kairos.WorkspaceMembershipRole,
    val effectiveRole: Kairos.WorkspaceMembershipRole,
)

@Serializable
data class KairosAgentWorkspaceGrantRequest(
    val workspaceId: Kairos.WorkspaceId,
    val role: Kairos.WorkspaceMembershipRole = Kairos.WorkspaceMembershipRole.Member,
)

@Serializable
data class KairosWorkspaceShareTarget(
    val user: Kairos.User,
    val username: String,
    val currentRole: Kairos.WorkspaceMembershipRole? = null,
)

@Serializable
data class KairosAdminUserRow(
    val user: Kairos.User,
    val username: String? = null,
    val lastAuthenticatedAt: Instant? = null,
    val workspaceAccess: List<KairosWorkspaceAccess> = emptyList(),
    val ownedAgentCount: Int = 0,
)

@Serializable
data class KairosAdminWorkspaceRow(
    val workspace: Kairos.Workspace,
    val memberCount: Int,
    val ownerCount: Int,
    val projectCount: Int,
    val ticketCount: Int,
    val objectiveCount: Int,
    val kpiCount: Int,
)

@Serializable
data class KairosAdminSnapshot(
    val users: List<KairosAdminUserRow> = emptyList(),
    val workspaces: List<KairosAdminWorkspaceRow> = emptyList(),
)

@Serializable data class PatchKairosAdminUserRequest(val role: Kairos.UserRole)

@Serializable data class CreateKairosSessionRequest(val username: String, val password: String)

@Serializable
data class CreateKairosSessionResponse(val token: String, val session: KairosSessionInfo)

@Serializable
data class CreateKairosAccountRequest(
    val displayName: String,
    val username: String,
    val password: String,
    val workspaceName: String,
)

@Serializable
data class CreateKairosAccountResponse(val token: String, val session: KairosSessionInfo)

@Serializable
data class PatchKairosUserRequest(
    val displayName: String? = null,
    val email: String? = null,
    val clearEmail: Boolean = false,
    val avatarUrl: String? = null,
    val clearAvatarUrl: Boolean = false,
    val timeZoneId: String? = null,
    val clearTimeZoneId: Boolean = false,
    val localeTag: String? = null,
    val clearLocaleTag: Boolean = false,
)

@Serializable
data class CreateKairosAgentSubaccountRequest(
    val displayName: String,
    val username: String,
    val workspaceAccess: List<KairosAgentWorkspaceGrantRequest> = emptyList(),
)

@Serializable data class PatchKairosAgentSubaccountRequest(val displayName: String? = null)

@Serializable
data class CreateKairosAgentSubaccountResponse(val agent: KairosAgentSubaccount, val token: String)

@Serializable
data class RotateKairosAgentSubaccountTokenResponse(
    val agent: KairosAgentSubaccount,
    val token: String,
)

@Serializable
data class CreateKairosWorkspaceMemberRequest(
    val username: String,
    val role: Kairos.WorkspaceMembershipRole = Kairos.WorkspaceMembershipRole.Member,
)

@Serializable data class ResolveKairosWorkspaceShareTargetRequest(val identifier: String)

@Serializable
data class ResolveKairosWorkspaceShareTargetResponse(val target: KairosWorkspaceShareTarget? = null)

@Serializable data class PatchKairosWorkspaceMemberRequest(val role: Kairos.WorkspaceMembershipRole)

@Serializable
data class CreateKairosProjectRequest(
    val name: String,
    val parentProjectId: Kairos.ProjectId? = null,
)

@Serializable
data class PatchKairosProjectRequest(
    val name: String? = null,
    val parentProjectId: Kairos.ProjectId? = null,
    val clearParent: Boolean = false,
)

@Serializable
data class CreateKairosTicketRequest(
    val projectId: Kairos.ProjectId,
    val title: String,
    val description: String = "",
    val assigneeUserId: Kairos.UserId? = null,
    val stage: Kairos.TicketStage = Kairos.TicketStage.Todo,
)

@Serializable data class CreateKairosTicketBlockerRequest(val blockingTicketId: Kairos.TicketId)

@Serializable
data class CreateKairosObjectiveRequest(
    val projectId: Kairos.ProjectId? = null,
    val description: String,
    val kpiTargets: List<Kairos.KpiWithTarget> = emptyList(),
    val deadline: String? = null,
    val tickets: List<Kairos.TicketId> = emptyList(),
)

@Serializable
data class PatchKairosObjectiveRequest(
    val description: String? = null,
    val kpiTargets: List<Kairos.KpiWithTarget>? = null,
    val deadline: String? = null,
    val tickets: List<Kairos.TicketId>? = null,
)

@Serializable
data class CreateKairosKpiRequest(
    val projectId: Kairos.ProjectId? = null,
    val name: String,
    val targetValue: Kairos.KpiTarget? = null,
    val origin: Kairos.KpiOrigin? = null,
    val frequency: Kairos.KpiFrequency = Kairos.KpiFrequency.Weekly,
    val unit: Kairos.KpiUnit = Kairos.KpiUnit.Count,
    val accumulationType: Kairos.KpiAccumulationType = Kairos.KpiAccumulationType.Summation,
)

@Serializable
data class PatchKairosKpiRequest(
    val name: String? = null,
    val targetValue: Kairos.KpiTarget? = null,
    val clearTarget: Boolean = false,
    val origin: Kairos.KpiOrigin? = null,
)

@Serializable
data class CreateKairosKpiMeasurementRequest(
    val value: Double,
    val recordedAt: ZonedDateTime? = null,
)

@Serializable
data class CreateKairosEventRequest(
    val projectId: Kairos.ProjectId? = null,
    val title: String,
    val summary: String,
    val dtStart: ZonedDateTime,
    val dtEnd: ZonedDateTime? = null,
    val description: String? = null,
    val location: String? = null,
)

@Serializable
data class PatchKairosEventRequest(
    val title: String? = null,
    val summary: String? = null,
    val dtStart: ZonedDateTime? = null,
    val dtEnd: ZonedDateTime? = null,
    val clearDtEnd: Boolean = false,
)

@Serializable
data class CreateKairosTimeboxRequest(
    val projectId: Kairos.ProjectId? = null,
    val tickets: List<Kairos.TicketId> = emptyList(),
    val description: String,
    val startTime: ZonedDateTime,
    val durationHours: Int? = null,
)

@Serializable
data class PatchKairosTimeboxRequest(
    val description: String? = null,
    val startTime: ZonedDateTime? = null,
    val durationHours: Int? = null,
    val clearRecordedDuration: Boolean = false,
)

@Serializable
data class CreateKairosRoutineRequest(
    val projectId: Kairos.ProjectId? = null,
    val description: String,
)

@Serializable data class PatchKairosRoutineRequest(val description: String? = null)

@Serializable
data class PatchKairosTicketRequest(
    val projectId: Kairos.ProjectId? = null,
    val title: String? = null,
    val description: String? = null,
    val completed: Boolean? = null,
    val assigneeUserId: Kairos.UserId? = null,
    val clearAssignee: Boolean = false,
    val stage: Kairos.TicketStage? = null,
    val priority: Kairos.TicketPriority? = null,
    val urgency: Kairos.TicketUrgency? = null,
    val deadline: ZonedDateTime? = null,
    val clearDeadline: Boolean = false,
)

@Serializable data class KairosErrorResponse(val message: String)

fun PatchKairosTicketRequest.completedAtModification(): Modification<kotlin.time.Instant?> =
    when (completed) {
        null -> Modification.Keep
        true -> Modification.Update(Clock.System.now())
        false -> Modification.Update(null)
    }

fun PatchKairosTicketRequest.deadlineModification(): Modification<ZonedDateTime?> =
    when {
        deadline != null -> Modification.Update(deadline)
        clearDeadline -> Modification.Update(null)
        else -> Modification.Keep
    }
